import type { SimpleTableConfig } from "@/components/shared/UniversalCRMTable/types";
import type { Social_Media_Posts } from "@/src/gql/graphql";
import type { EntityFormSectionConfig } from "@/lib/types/forms/entity-form-sections";
import { socialMediaPostDetailActions } from "./SocialMediaPostDetailConfig";
import { socialMediaPostSavedViews } from "@/features/social-media-posts/config/saved-views";
import {
  SOCIAL_POST_STATUS_OPTIONS,
  SOCIAL_POST_BRAND_OPTIONS,
  SOCIAL_POST_MEDIA_KIND_OPTIONS,
  SOCIAL_POST_PLATFORM_OPTIONS,
} from "@/features/social-media-posts/config/options";
import {
  textField,
  selectField,
  datetimeField,
  fkField,
  STANDARD_TABLE_FEATURES,
  standardCrmBulkActions,
} from "@/features/crm-table-config/table-config-builders";

/**
 * Table config for the `social_media_posts` entity — modeled on
 * `ContractsConfig.ts` (contracts). This `fields` map is the single source of
 * truth for both list columns AND the detail modal's "fields" tab.
 *
 * Posts are usually authored/published by the marketing pipeline, but the CRM
 * surface also supports authoring a post by hand: `actions.create` is true and
 * the authoring fields are `editable`. System-managed fields (`published_at`,
 * `error_summary`) stay `editable: false` — the publisher owns them.
 */

/** Progressive-disclosure create-form layout (kept minimal; create is not the primary path). */
const socialMediaPostCreateFormSections = (): readonly EntityFormSectionConfig[] => [
  {
    id: "core",
    kind: "always",
    title: "Post",
    // `status` is intentionally NOT here: it's owned by the publish pipeline, not
    // authored. A new post always starts draft (or scheduled if scheduled_at is
    // set) via deriveInitialPostStatus, and only "Post now" moves it forward.
    fieldNames: ["title", "body", "brand", "platforms", "media_kind"],
  },
  {
    id: "scheduling",
    kind: "collapsible",
    title: "Scheduling",
    // `published_at` is system-managed (set by the publishing cron), so it is
    // not editable and never appears in the create form.
    fieldNames: ["scheduled_at"],
  },
  {
    id: "classification",
    kind: "collapsible",
    title: "Details",
    fieldNames: ["tags", "location", "media_url", "thumbnail_url", "company_id", "contact_id"],
  },
];

export const SOCIAL_MEDIA_POSTS_CONFIG: SimpleTableConfig<Social_Media_Posts> = {
  entity: "social_media_posts",
  displayName: "Social Post",
  statusFieldId: "status",
  defaultPageSize: 25,
  defaultSort: { field: "scheduled_at", direction: "desc" },
  rowActionsPlacement: "end",
  rowActionsDisplayMode: "hover",

  fields: {
    title: textField({
      label: "Title",
      sortable: true,
      filterable: true,
      searchable: true,
      resizable: true,
      hideable: true,
      editable: true,
      primary: true,
      filterType: "contains",
      width: 240,
      minWidth: 160,
      maxWidth: 400,
      mobileDetailSectionId: "core",
      icon: "FileText",
    }),
    body: {
      type: "textarea",
      label: "Body",
      sortable: false,
      filterable: false,
      searchable: true,
      resizable: true,
      hideable: true,
      editable: true,
      required: true,
      placeholder: "What do you want to post?",
      width: 320,
      minWidth: 200,
      maxWidth: 520,
      mobileDetailSectionId: "core",
      icon: "AlignLeft",
    },
    status: selectField({
      label: "Status",
      isStatusField: true,
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true,
      // Read-only: status is owned by the publish pipeline (draft → scheduled →
      // publishing → posted/…). Editing it by hand never published, so inline
      // editing is disabled and there is no "Change status" action. Use
      // "Post now" to publish. Options remain for badge colors + filter chips.
      editable: false,
      badge: true,
      groupable: true,
      filterType: "exact",
      width: 150,
      minWidth: 120,
      maxWidth: 200,
      options: SOCIAL_POST_STATUS_OPTIONS.map((o) => ({ label: o.label, value: o.value })),
      mobileDetailSectionId: "core",
      icon: "CircleDot",
    }),
    brand: selectField({
      label: "Brand",
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true,
      editable: true,
      badge: true,
      groupable: true,
      filterType: "exact",
      width: 200,
      minWidth: 140,
      maxWidth: 300,
      options: SOCIAL_POST_BRAND_OPTIONS.map((o) => ({ label: o.label, value: o.value })),
      mobileDetailSectionId: "core",
      icon: "Building2",
    }),
    platforms: {
      type: "array",
      label: "Platforms",
      sortable: false,
      filterable: true,
      resizable: true,
      hideable: true,
      editable: true,
      filterType: "exact",
      width: 200,
      minWidth: 140,
      maxWidth: 320,
      options: SOCIAL_POST_PLATFORM_OPTIONS.map((o) => ({ label: o.label, value: o.value })),
      mobileDetailSectionId: "core",
      icon: "Share2",
    },
    media_kind: selectField({
      label: "Media",
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true,
      editable: true,
      filterType: "exact",
      width: 130,
      minWidth: 100,
      maxWidth: 180,
      options: SOCIAL_POST_MEDIA_KIND_OPTIONS.map((o) => ({ label: o.label, value: o.value })),
      mobileDetailSectionId: "core",
      icon: "Image",
    }),
    media_url: textField({
      label: "Media URL",
      sortable: false,
      filterable: false,
      searchable: false,
      resizable: true,
      hideable: true,
      editable: true,
      width: 200,
      minWidth: 140,
      maxWidth: 320,
      mobileDetailSectionId: "classification",
      icon: "Link",
    }),
    thumbnail_url: textField({
      label: "Thumbnail URL",
      sortable: false,
      filterable: false,
      searchable: false,
      resizable: true,
      hideable: true,
      editable: true,
      width: 200,
      minWidth: 140,
      maxWidth: 320,
      mobileDetailSectionId: "classification",
      icon: "Image",
    }),
    tags: {
      type: "tags",
      label: "Tags",
      sortable: false,
      filterable: true,
      resizable: true,
      hideable: true,
      editable: true,
      filterType: "exact",
      width: 200,
      minWidth: 140,
      maxWidth: 320,
      mobileDetailSectionId: "classification",
      icon: "Tag",
    },
    location: textField({
      label: "Location",
      sortable: true,
      filterable: true,
      searchable: true,
      resizable: true,
      hideable: true,
      editable: true,
      filterType: "contains",
      width: 180,
      minWidth: 120,
      maxWidth: 280,
      mobileDetailSectionId: "classification",
      icon: "MapPin",
    }),
    scheduled_at: datetimeField({
      label: "Scheduled",
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true,
      editable: true,
      filterType: "dateRange",
      width: 170,
      minWidth: 130,
      maxWidth: 220,
      mobileDetailSectionId: "scheduling",
      icon: "Calendar",
    }),
    published_at: datetimeField({
      label: "Published",
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true,
      // System-managed: set by the publishing cron, never authored in the form.
      editable: false,
      filterType: "dateRange",
      width: 170,
      minWidth: 130,
      maxWidth: 220,
      mobileDetailSectionId: "scheduling",
      icon: "CalendarCheck",
    }),
    company_id: fkField("company-select", {
      label: "Company",
      foreignKey: {
        referencedTable: "companies",
        referencedColumn: "id",
        constraintName: "social_media_posts_company_id_fkey",
        onDelete: "SET NULL",
        onUpdate: "NO ACTION",
        relationKind: "many-to-one",
      },
      sortable: false,
      filterable: true,
      resizable: true,
      hideable: true,
      editable: true,
      filterType: "exact",
      width: 220,
      minWidth: 160,
      maxWidth: 360,
      mobileDetailSectionId: "classification",
      icon: "Building2",
    }),
    contact_id: fkField("client-select", {
      label: "Contact",
      foreignKey: {
        referencedTable: "contacts",
        referencedColumn: "id",
        constraintName: "social_media_posts_contact_id_fkey",
        onDelete: "SET NULL",
        onUpdate: "NO ACTION",
        relationKind: "many-to-one",
      },
      sortable: false,
      filterable: true,
      resizable: true,
      hideable: true,
      editable: true,
      filterType: "exact",
      width: 220,
      minWidth: 160,
      maxWidth: 360,
      mobileDetailSectionId: "classification",
      icon: "User",
    }),
    error_summary: textField({
      label: "Error",
      sortable: false,
      filterable: false,
      searchable: false,
      resizable: true,
      hideable: true,
      // System-managed: written by the publisher on failure, never authored.
      editable: false,
      width: 240,
      minWidth: 160,
      maxWidth: 400,
      mobileDetailSectionId: "scheduling",
      icon: "AlertTriangle",
    }),
  },

  features: { ...STANDARD_TABLE_FEATURES },

  actions: {
    create: true,
    edit: true,
    delete: true,
    view: true,
  },

  detailActions: socialMediaPostDetailActions,
  bulkActions: standardCrmBulkActions<Social_Media_Posts>(),
  savedViews: socialMediaPostSavedViews,
  createFormSections: socialMediaPostCreateFormSections,
  customFilterHooks: {},

  mobileDetailSections: [
    { id: "core", label: "Post" },
    { id: "scheduling", label: "Scheduling" },
    { id: "classification", label: "Details" },
  ],
};
